import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { authAPI } from '../../services/api';
import { 
  FiUsers, 
  FiTruck, 
  FiClock, 
  FiCheckCircle,
  FiAlertCircle,
  FiTrendingUp,
  FiPackage,
  FiMail,
  FiFilter,
  FiX
} from 'react-icons/fi';

const AdminDashboard = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['admin-dashboard-stats', selectedCustomer],
    () => authAPI.getAdminDashboardStats(selectedCustomer ? { customer_id: selectedCustomer } : {})
  );

  const { data: pendingShipments, isLoading: shipmentsLoading } = useQuery(
    ['admin-pending-shipments', selectedCustomer],
    () => authAPI.getPendingShipments(selectedCustomer ? { customer_id: selectedCustomer } : {})
  );

  const { data: customers } = useQuery(
    'admin-customers',
    () => authAPI.getCustomers(),
    {
      select: (data) => {
        // Ensure we always return an array
        if (Array.isArray(data)) {
          return data;
        }
        if (data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
    }
  );

  const statCards = [
    {
      name: 'Total Users',
      value: stats?.total_users || 0,
      icon: FiUsers,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      name: 'Total Shipments',
      value: stats?.total_shipments || 0,
      icon: FiTruck,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      name: 'Pending Processing',
      value: stats?.pending_shipments || 0,
      icon: FiClock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      name: 'Delivered',
      value: stats?.delivered_shipments || 0,
      icon: FiCheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'customer_packaging':
      case 'boxes_created':
        return 'status-pending';
      case 'ready_for_origin_warehouse':
      case 'received_in_india':
      case 'partially_received_india':
      case 'scanned_in_india':
      case 'international_labels_created':
        return 'status-processing';
      case 'shipped_to_usa':
      case 'in_transit_to_usa':
        return 'status-in-transit';
      case 'ready_for_destination_warehouse':
      case 'received_in_usa':
      case 'partially_received_usa':
      case 'scanned_in_usa':
      case 'last_mile_labels_created':
        return 'status-processing';
      case 'handed_to_carrier':
      case 'last_mile_in_transit':
        return 'status-in-transit';
      case 'delivered':
        return 'status-delivered';
      case 'exception':
        return 'status-exception';
      default:
        return 'status-pending';
    }
  };

  if (statsLoading || shipmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of the shipment management system
          </p>
        </div>
        
        {/* Customer Filter */}
        <div className="flex items-center space-x-4">
          {selectedCustomer && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Filtered by: {customers?.find(c => c.id.toString() === selectedCustomer)?.company_name || 'Customer'}
              </span>
              <button
                onClick={() => setSelectedCustomer('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowCustomerFilter(!showCustomerFilter)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <FiFilter className="h-4 w-4 mr-2" />
            Filter by Customer
          </button>
        </div>
      </div>

      {/* Customer Filter Dropdown */}
      {showCustomerFilter && (
        <div className="bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Select Customer</h3>
          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
            <button
              onClick={() => {
                setSelectedCustomer('');
                setShowCustomerFilter(false);
              }}
              className={`text-left px-3 py-2 text-sm rounded-md ${
                selectedCustomer === '' 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              All Customers
            </button>
            {customers && Array.isArray(customers) && customers.map((customer) => (
              <button
                key={customer.id}
                onClick={() => {
                  setSelectedCustomer(customer.id.toString());
                  setShowCustomerFilter(false);
                }}
                className={`text-left px-3 py-2 text-sm rounded-md ${
                  selectedCustomer === customer.id.toString() 
                    ? 'bg-blue-100 text-blue-900' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="font-medium">{customer.company_name}</div>
                <div className="text-xs text-gray-500">{customer.username}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${stat.bgColor}`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending Shipments */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {selectedCustomer ? 'Customer Shipments' : 'Pending Shipments'}
          </h3>
          {pendingShipments?.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shipment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Child Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingShipments.slice(0, 10).map((shipment) => (
                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiPackage className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {shipment.master_tracking_number || 'No tracking number'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {shipment.origin_address?.substring(0, 30)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{shipment.user?.company_name}</div>
                        <div className="text-sm text-gray-500">{shipment.user?.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-badge ${getStatusColor(shipment.status)}`}>
                          {shipment.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.child_shipments?.length || 0} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(shipment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <a
                          href={`/g-admin/shipments?shipment=${shipment.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Process
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <FiCheckCircle className="mx-auto h-12 w-12 text-green-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">All caught up!</h3>
              <p className="mt-1 text-sm text-gray-500">
                No pending shipments to process.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <a
              href="/g-admin/shipments"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-red-500 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-red-50 text-red-700 ring-4 ring-white">
                  <FiTruck className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Process Shipments
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Add tracking numbers and labels
                </p>
              </div>
            </a>

            <a
              href="/g-admin/users"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-red-500 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                  <FiUsers className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Manage Users
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  View and manage customer accounts
                </p>
              </div>
            </a>

            <a
              href="/g-admin/invites"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-red-500 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-green-50 text-green-700 ring-4 ring-white">
                  <FiMail className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Send Invites
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Invite new customers to the platform
                </p>
              </div>
            </a>

            <a
              href="/g-admin/settings"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-red-500 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-purple-50 text-purple-700 ring-4 ring-white">
                  <FiTrendingUp className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  System Settings
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Configure system preferences
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
